import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface BambooType {
  name: string;
  height: string;
  diameter: string;
  usage: string;
  color: string;
}

interface EngineeringProp {
  label: string;
  value: string;
  percent: number;
  color: string;
}

interface Virtue {
  char: string;
  meaning: string;
}

interface AppIcon {
  name: string;
  icon: string;
}

interface GrowthStage {
  label: string;
  duration: string;
  desc: string;
}

interface SustainabilityBar {
  label: string;
  bamboo: number;
  timber: number;
}

interface BambooTemplateProps {
  topic: string;
  subtitle: string;
  bambooTypes: BambooType[];
  engineering: EngineeringProp[];
  virtues: Virtue[];
  applications: AppIcon[];
  growthCycle: GrowthStage[];
  sustainability: SustainabilityBar[];
  stats: { value: string; label: string }[];
  bottomWisdom: string;
}

const DEFAULT_PROPS: BambooTemplateProps = {
  topic: '竹文化与竹工程',
  subtitle: 'Bamboo Culture & Engineering',
  bambooTypes: [
    { name: '毛竹', height: '20m+', diameter: '15cm', usage: '建筑·造纸', color: '#22c55e' },
    { name: '紫竹', height: '8m', diameter: '4cm', usage: '乐器·观赏', color: '#7c3aed' },
    { name: '楠竹', height: '18m', diameter: '12cm', usage: '家具·工艺', color: '#65a30d' },
    { name: '斑竹', height: '12m', diameter: '6cm', usage: '装饰·文化', color: '#d97706' },
  ],
  engineering: [
    { label: '抗拉强度', value: '≈ 钢材', percent: 95, color: '#22c55e' },
    { label: '抗压强度', value: '> 混凝土', percent: 80, color: '#059669' },
    { label: '生长速度', value: '91cm/天', percent: 98, color: '#65a30d' },
    { label: 'CO₂吸收', value: '35%多于树', percent: 72, color: '#16a34a' },
  ],
  virtues: [
    { char: '正直', meaning: '身正不倚' },
    { char: '奋进', meaning: '节节高升' },
    { char: '虚怀', meaning: '中空有节' },
    { char: '质朴', meaning: '外直中通' },
    { char: '卓尔', meaning: '凌霜傲雪' },
    { char: '善群', meaning: '聚而成林' },
    { char: '担当', meaning: '可任栋梁' },
  ],
  applications: [
    { name: '建筑', icon: '🏗️' },
    { name: '家具', icon: '🪑' },
    { name: '乐器', icon: '🎵' },
    { name: '造纸', icon: '📜' },
    { name: '食品', icon: '🥢' },
    { name: '工艺品', icon: '🎋' },
  ],
  growthCycle: [
    { label: '蓄力期', duration: '3年', desc: '根系深扎·积蓄能量' },
    { label: '疯长期', duration: '60天', desc: '日长91cm·直冲云霄' },
    { label: '成材期', duration: '5年', desc: '纤维致密·可堪大用' },
  ],
  sustainability: [
    { label: '生长速度', bamboo: 95, timber: 30 },
    { label: '碳汇能力', bamboo: 85, timber: 55 },
    { label: '再生性', bamboo: 98, timber: 20 },
  ],
  stats: [
    { value: '1500+', label: '全球竹种类' },
    { value: '91cm', label: '日最快生长' },
    { value: '3500万公顷', label: '全球竹林面积' },
  ],
  bottomWisdom: '虚心竹有低头叶，傲骨梅无仰面花',
};

const SECTION_TITLE: React.CSSProperties = {
  fontSize: 26,
  color: '#4a7c59',
  marginBottom: 10,
  letterSpacing: '0.1em',
  fontWeight: 600,
};

export const BambooTemplate: React.FC<Partial<BambooTemplateProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerIn = spring({ frame, fps, config: { damping: 18, stiffness: 80 } });

  const typeStart = 35;
  const engStart = 55;
  const virtueStart = 75;
  const appStart = 90;
  const cycleStart = 65;
  const sustainStart = 100;
  const statsStart = 15;
  const wisdomStart = 160;

  const wisdomOpacity = interpolate(frame, [wisdomStart, wisdomStart + 30], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const crossSectionDraw = interpolate(frame, [20, 80], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const leafUnfurl = (idx: number) =>
    spring({
      frame: frame - (25 + idx * 16),
      fps,
      config: { damping: 12, stiffness: 90 },
    });

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(135deg, #0f1a0a 0%, #0a140f 60%, #0d1f0d 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Noto Sans SC", sans-serif',
        color: 'white',
        overflow: 'hidden',
        width: 1920,
        height: 1080,
      }}
    >
      {/* Background bamboo node lines */}
      {Array.from({ length: 14 }).map((_, i) => {
        const x = 60 + i * 140;
        return (
          <div
            key={`line-${i}`}
            style={{
              position: 'absolute',
              left: x,
              top: 0,
              width: 1,
              height: 1080,
              background: '#22c55e',
              opacity: 0.04,
            }}
          >
            {Array.from({ length: 7 }).map((_, j) => (
              <div
                key={`node-${i}-${j}`}
                style={{
                  position: 'absolute',
                  top: 50 + j * 150,
                  left: -4,
                  width: 9,
                  height: 3,
                  background: '#22c55e',
                  opacity: 0.08,
                  borderRadius: 2,
                }}
              />
            ))}
          </div>
        );
      })}

      {/* ===== Left Decorative Bamboo Stalk ===== */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: -50,
          width: 250,
          height: 1080,
          background: 'linear-gradient(90deg, rgba(10,18,10,0.85) 0%, transparent 100%)',
        }}
      >
        <svg
          style={{ position: 'absolute', left: 55, top: 30 }}
          width="140"
          height="1020"
          viewBox="0 0 140 1020"
        >
          {Array.from({ length: 10 }).map((_, i) => {
            const segY = 960 - i * 92;
            const segOpacity = interpolate(frame, [6 + i * 11, 22 + i * 11], [0, 1], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            });
            const segScale = interpolate(frame, [6 + i * 11, 26 + i * 11], [0, 1], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            });
            return (
              <g key={`seg-${i}`} opacity={segOpacity}>
                <rect
                  x="50"
                  y={segY}
                  width="40"
                  height={92 * segScale}
                  rx="4"
                  fill="url(#stalkGrad)"
                />
                <ellipse
                  cx="70"
                  cy={segY + 92 * segScale}
                  rx="24"
                  ry="4"
                  fill="#78350f"
                  opacity={0.7 * segScale}
                />
              </g>
            );
          })}
          <defs>
            <linearGradient id="stalkGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#65a30d" />
              <stop offset="50%" stopColor="#22c55e" />
              <stop offset="100%" stopColor="#059669" />
            </linearGradient>
          </defs>
          {[
            { cx: 90, cy: 870, rot: 25, side: 1 },
            { cx: 50, cy: 778, rot: -30, side: -1 },
            { cx: 90, cy: 686, rot: 20, side: 1 },
            { cx: 50, cy: 594, rot: -25, side: -1 },
            { cx: 90, cy: 502, rot: 22, side: 1 },
            { cx: 50, cy: 410, rot: -28, side: -1 },
            { cx: 90, cy: 318, rot: 18, side: 1 },
            { cx: 50, cy: 226, rot: -22, side: -1 },
          ].map((leaf, i) => {
            const lu = leafUnfurl(i);
            return (
              <g
                key={`leaf-${i}`}
                transform={`translate(${leaf.cx}, ${leaf.cy}) rotate(${leaf.rot * lu}) scale(${lu})`}
                opacity={lu}
              >
                <path
                  d={`M0,0 Q${leaf.side * 30},-10 ${leaf.side * 50},-5 Q${leaf.side * 30},0 0,0`}
                  fill="#059669"
                  opacity="0.85"
                />
                <path
                  d={`M0,0 Q${leaf.side * 24},-18 ${leaf.side * 40},-14 Q${leaf.side * 18},-7 0,0`}
                  fill="#22c55e"
                  opacity="0.6"
                />
              </g>
            );
          })}
        </svg>
      </div>

      {/* ===== Header ===== */}
      <div
        style={{
          position: 'absolute',
          top: 26,
          left: 285,
          right: 40,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          opacity: headerIn,
          transform: `translateY(${(1 - headerIn) * 15}px)`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div
            style={{
              display: 'inline-block',
              padding: '4px 14px',
              background: '#14532d',
              borderRadius: 6,
              fontSize: 22,
              fontWeight: 700,
              color: '#22c55e',
              border: '1px solid #22c55e30',
              flexShrink: 0,
            }}
          >
            🎋 竹学
          </div>
          <h1 style={{ fontSize: 36, fontWeight: 900, margin: 0, color: '#f0fdf4', flexShrink: 0 }}>
            {p.topic}
          </h1>
          <p style={{ fontSize: 24, color: '#6b7280', margin: 0, flexShrink: 0 }}>{p.subtitle}</p>
        </div>
        <div style={{ display: 'flex', gap: 14, flexShrink: 0 }}>
          {p.stats.map((st, i) => {
            const stIn = spring({
              frame: frame - (statsStart + i * 10),
              fps,
              config: { damping: 14, stiffness: 100 },
            });
            return (
              <div
                key={i}
                style={{
                  background: '#0f1a0a',
                  borderRadius: 10,
                  padding: '10px 18px',
                  textAlign: 'center',
                  border: '1px solid #22c55e20',
                  opacity: stIn,
                  transform: `scale(${0.85 + 0.15 * stIn})`,
                }}
              >
                <div style={{ fontSize: 22, fontWeight: 900, color: '#22c55e' }}>{st.value}</div>
                <div style={{ fontSize: 18, color: '#6b7280', marginTop: 2 }}>{st.label}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Separator */}
      <div
        style={{
          position: 'absolute',
          top: 120,
          left: 285,
          right: 40,
          height: 2,
          background: 'linear-gradient(90deg, #22c55e30, #22c55e08)',
        }}
      />

      {/* ===== Column 1: Cross-section + Engineering (x: 285, w: 440) ===== */}

      {/* Cross-section Diagram */}
      <div style={{ position: 'absolute', top: 140, left: 285, width: 440 }}>
        <div style={SECTION_TITLE}>横截面结构</div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
          <svg width="300" height="320" viewBox="0 -25 250 275">
            <circle
              cx="125" cy="125" r={110 * crossSectionDraw}
              fill="none" stroke="#65a30d" strokeWidth="12" opacity={crossSectionDraw * 0.9}
            />
            {Array.from({ length: 12 }).map((_, i) => {
              const angle = (i / 12) * Math.PI * 2;
              return (
                <circle
                  key={`vb-${i}`}
                  cx={125 + Math.cos(angle) * 75}
                  cy={125 + Math.sin(angle) * 75}
                  r={6 * crossSectionDraw}
                  fill="#22c55e"
                  opacity={crossSectionDraw * 0.7}
                />
              );
            })}
            {Array.from({ length: 6 }).map((_, i) => {
              const angle = (i / 6) * Math.PI * 2 + 0.3;
              return (
                <circle
                  key={`vbi-${i}`}
                  cx={125 + Math.cos(angle) * 48}
                  cy={125 + Math.sin(angle) * 48}
                  r={4 * crossSectionDraw}
                  fill="#059669"
                  opacity={crossSectionDraw * 0.6}
                />
              );
            })}
            <circle
              cx="125" cy="125" r={25 * crossSectionDraw}
              fill="#0a140f" stroke="#78350f" strokeWidth="2" opacity={crossSectionDraw}
            />
            <text x="125" y="132" textAnchor="middle" fill="#6b7280" fontSize="20" opacity={crossSectionDraw}>
              中空
            </text>
            <text x="125" y="-5" textAnchor="middle" fill="#65a30d" fontSize="20" opacity={crossSectionDraw}>
              外皮层
            </text>
            <text x="230" y="132" textAnchor="end" fill="#22c55e" fontSize="20" opacity={crossSectionDraw}>
              维管束
            </text>
          </svg>
          <div style={{ fontSize: 22, color: '#6b7280', lineHeight: 1.6, textAlign: 'center' }}>
            中空结构赋予竹子极高的
            <span style={{ color: '#22c55e', fontWeight: 600 }}>强度重量比</span>
            ，维管束提供纵向抗拉强度，外皮层致密坚硬
          </div>
        </div>
      </div>

      {/* Engineering Properties */}
      <div style={{ position: 'absolute', top: 620, left: 285, width: 440 }}>
        <div style={SECTION_TITLE}>工程力学特性</div>
        {p.engineering.map((eng, i) => {
          const s = spring({
            frame: frame - (engStart + i * 14),
            fps,
            config: { damping: 14, stiffness: 120 },
          });
          const barWidth = interpolate(
            frame,
            [engStart + i * 14, engStart + i * 14 + 35],
            [0, eng.percent],
            { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
          );
          return (
            <div key={i} style={{ marginBottom: 14, opacity: s, transform: `translateX(${(1 - s) * -15}px)` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                <span style={{ fontSize: 20, fontWeight: 700, color: '#e2e8f0' }}>{eng.label}</span>
                <span style={{ fontSize: 20, color: eng.color, fontWeight: 600 }}>{eng.value}</span>
              </div>
              <div style={{ width: '100%', height: 9, background: '#1a2e1a', borderRadius: 4 }}>
                <div
                  style={{
                    width: `${barWidth}%`,
                    height: '100%',
                    background: `linear-gradient(90deg, ${eng.color}88, ${eng.color})`,
                    borderRadius: 4,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== Column 2: Bamboo Types + Virtues (x: 760, w: 500) ===== */}

      {/* Bamboo Types 2×2 */}
      <div style={{ position: 'absolute', top: 140, left: 760, width: 500 }}>
        <div style={SECTION_TITLE}>四大名竹</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {p.bambooTypes.map((bt, i) => {
            const s = spring({
              frame: frame - (typeStart + i * 14),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            return (
              <div
                key={i}
                style={{
                  width: 'calc(50% - 5px)',
                  background: '#0f1a0a',
                  borderRadius: 10,
                  padding: '14px 12px',
                  border: `1px solid ${bt.color}30`,
                  opacity: s,
                  transform: `translateY(${(1 - s) * 15}px)`,
                  textAlign: 'center',
                }}
              >
                <div style={{ fontSize: 20, fontWeight: 800, color: bt.color }}>{bt.name}</div>
                <div style={{ fontSize: 20, color: '#6b7280', marginTop: 5 }}>
                  高 {bt.height} · 径 {bt.diameter}
                </div>
                <div style={{ fontSize: 18, color: bt.color, marginTop: 5, opacity: 0.8 }}>{bt.usage}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 竹之七德 */}
      <div style={{ position: 'absolute', top: 460, left: 760, width: 500 }}>
        <div style={SECTION_TITLE}>竹之七德</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {p.virtues.map((v, i) => {
            const s = spring({
              frame: frame - (virtueStart + i * 8),
              fps,
              config: { damping: 12, stiffness: 100 },
            });
            return (
              <div
                key={i}
                style={{
                  background: '#14532d20',
                  border: '1px solid #22c55e30',
                  borderRadius: 8,
                  padding: '8px 14px',
                  textAlign: 'center',
                  opacity: s,
                  transform: `scale(${0.8 + 0.2 * s})`,
                  minWidth: 80,
                }}
              >
                <div style={{ fontSize: 20, fontWeight: 900, color: '#22c55e' }}>{v.char}</div>
                <div style={{ fontSize: 18, color: '#6b7280', marginTop: 1 }}>{v.meaning}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Applications */}
      <div style={{ position: 'absolute', top: 680, left: 760, width: 500 }}>
        <div style={SECTION_TITLE}>竹之六艺</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {p.applications.map((app, i) => {
            const s = spring({
              frame: frame - (appStart + i * 10),
              fps,
              config: { damping: 14, stiffness: 110 },
            });
            return (
              <div
                key={i}
                style={{
                  width: 'calc(33.33% - 7px)',
                  background: '#0f1a0a',
                  borderRadius: 10,
                  padding: '14px 8px',
                  textAlign: 'center',
                  border: '1px solid #22c55e20',
                  opacity: s,
                  transform: `scale(${0.85 + 0.15 * s})`,
                }}
              >
                <div style={{ fontSize: 28 }}>{app.icon}</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#e2e8f0', marginTop: 4 }}>
                  {app.name}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ===== Column 3: Growth + Sustainability (x: 1300, right: 40) ===== */}

      {/* Growth Cycle */}
      <div style={{ position: 'absolute', top: 140, left: 1300, right: 40 }}>
        <div style={SECTION_TITLE}>生长周期</div>
        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
          {p.growthCycle.map((stage, i) => {
            const s = spring({
              frame: frame - (cycleStart + i * 18),
              fps,
              config: { damping: 14, stiffness: 100 },
            });
            const lineProgress = interpolate(
              frame,
              [cycleStart + i * 18, cycleStart + i * 18 + 25],
              [0, 1],
              { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
            );
            return (
              <React.Fragment key={i}>
                <div
                  style={{
                    flex: 1,
                    textAlign: 'center',
                    opacity: s,
                    transform: `translateY(${(1 - s) * 12}px)`,
                  }}
                >
                  <div
                    style={{
                      width: 72,
                      height: 72,
                      borderRadius: '50%',
                      background: '#14532d',
                      border: '3px solid #22c55e',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto',
                      fontSize: 20,
                      fontWeight: 900,
                      color: '#22c55e',
                    }}
                  >
                    {stage.duration}
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#e2e8f0', marginTop: 10 }}>
                    {stage.label}
                  </div>
                  <div style={{ fontSize: 20, color: '#6b7280', marginTop: 4 }}>{stage.desc}</div>
                </div>
                {i < p.growthCycle.length - 1 && (
                  <div
                    style={{
                      width: 36,
                      height: 3,
                      marginTop: 36,
                      background: `linear-gradient(90deg, #22c55e, #22c55e ${lineProgress * 100}%, #1a2e1a ${lineProgress * 100}%)`,
                      flexShrink: 0,
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Sustainability Comparison */}
      <div style={{ position: 'absolute', top: 365, left: 1300, right: 40 }}>
        <div style={SECTION_TITLE}>竹 vs 木材 · 可持续性对比</div>
        {p.sustainability.map((item, i) => {
          const s = spring({
            frame: frame - (sustainStart + i * 16),
            fps,
            config: { damping: 14, stiffness: 100 },
          });
          const bambooBar = interpolate(
            frame,
            [sustainStart + i * 16, sustainStart + i * 16 + 35],
            [0, item.bamboo],
            { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
          );
          const timberBar = interpolate(
            frame,
            [sustainStart + i * 16 + 8, sustainStart + i * 16 + 43],
            [0, item.timber],
            { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
          );
          return (
            <div key={i} style={{ marginBottom: 16, opacity: s }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#e2e8f0', marginBottom: 5 }}>
                {item.label}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 20, color: '#22c55e', width: 24 }}>竹</span>
                <div style={{ flex: 1, height: 10, background: '#1a2e1a', borderRadius: 4 }}>
                  <div
                    style={{
                      width: `${bambooBar}%`,
                      height: '100%',
                      background: 'linear-gradient(90deg, #059669, #22c55e)',
                      borderRadius: 4,
                    }}
                  />
                </div>
                <span style={{ fontSize: 20, color: '#6b7280', width: 30, textAlign: 'right' }}>
                  {Math.round(bambooBar)}%
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 20, color: '#78350f', width: 24 }}>木</span>
                <div style={{ flex: 1, height: 10, background: '#1a2e1a', borderRadius: 4 }}>
                  <div
                    style={{
                      width: `${timberBar}%`,
                      height: '100%',
                      background: 'linear-gradient(90deg, #92400e, #78350f)',
                      borderRadius: 4,
                    }}
                  />
                </div>
                <span style={{ fontSize: 20, color: '#6b7280', width: 30, textAlign: 'right' }}>
                  {Math.round(timberBar)}%
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== Bottom Wisdom ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 8,
          left: 285,
          right: 40,
          background: '#0f1a0aee',
          borderRadius: 12,
          padding: '12px 22px',
          borderTop: '2px solid #22c55e',
          opacity: wisdomOpacity,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 22 }}>🎋</span>
          <span style={{ fontSize: 20, color: '#e2e8f0', lineHeight: 1.5, fontWeight: 500 }}>
            {p.bottomWisdom}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 250], [0, 100], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}%`,
          height: 3,
          background: 'linear-gradient(90deg, #065f46, #22c55e, #65a30d)',
        }}
      />
    </AbsoluteFill>
  );
};
